import { Button, Dialog } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSwitch } from 'src/js/component/base/form/form-switch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { constant } from 'src/js/constant';
import { scrollToId } from 'src/js/helper/scroll-helper';
import { useMutable } from 'src/js/hook/use-mutable';
import { createListStore, ListSelector } from '../../util/listSelector';
import { PresetDeleteModal } from './preset-delete-modal';
import { PresetEditModal } from './preset-edit-modal';
import { presetNames } from './preset-helper';
import { PresetImport } from './preset-import';
import { PresetViewModel, Presets, newPresetViewModel, Preset } from './preset-models';
import { v4 as uuidv4 } from 'uuid';
import presetsModel, {
  createPreset,
  duplicatePreset,
  fetchPresets,
  loadPreset,
  renamePreset,
  setAutoSavePreset,
  setStartupPreset,
  uploadPreset,
} from './preset-store';
import { PresetManagerTable } from './preset-manager-table';
import { triggerEvent } from '../../events';
import { RebootNeededNotSavedModal } from '../../reboot/reboot-needed-not-saved-modal';
import { useLocalStorage } from 'usehooks-ts';
import { notificationHandler } from '../../notification/notification-handler';
import { createAndDispatchNotification } from '../../notification/notification-helper';
import { NotificationVariant } from '../../notification/notification';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { useWatchObject } from 'src/js/store/use-watch';
import { TwoButtonsModal } from 'src/js/modals/two-buttons-modal';

const SHOW_NEW_PRESET_BUTTON = false; /* New Preset is not shown as of 28.Nov.2023 */

interface Props {
  presets: Presets;
  onCancel: VoidFunction;
  shouldReloadData: VoidFunction;
}

export const PresetManager: React.FunctionComponent<Props> = ({ presets, onCancel, shouldReloadData }) => {
  const { t } = useTranslation();
  const [singleItem, setSingleItem] = useMutable<Preset>(undefined); // for kebab selection
  let listController: ListSelector<Preset> = null;
  const [uuid] = useMutable(uuidv4());
  listController = createListStore(presets.data, uuid(), 'name');
  const [savePromptModalShown, showSavePromptModal, hideSavePromptModal] = useModalControls(); // FIXME rename to upload related
  const [savePromptFromLoadModalShown, showSavePromptFromLoadModal, hideSavePromptFromLoadModal] = useModalControls();
  const [reloadWarningModalShown, showReloadWarningModal, hideReloadWarningModal] = useModalControls(); 
  const [presetLoadParam2, setPresetLoadParam2] = useMutable(true);
  const [selectedFile, setSelectedFile] = useMutable(null);
  const [hiliteFile, setHiliteFile] = useState(null);
  const sortDirectionKey = `${constant.lStoragePrefix}:presetManager:ActivateImportedPreset`;
  const [activateImportedPreset, setActivateImportedPreset] = useLocalStorage(sortDirectionKey, false);
  const [importShown, showImport, hideImport] = useModalControls();
  const [newShown, showNew, hideNew] = useModalControls();
  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const [renameShown, showRename, hideRename] = useModalControls();
  const [importButtonState, setImportButtonState] = useState<ButtonStateType>(undefined);

  useWatchObject(listController, (_e) => {
    if (singleItem() != null) {
      setSingleItem(undefined); // single item is stale, dont use it (NKE-3606)
    }
  });

  useWatchObject(presetsModel, (_presets: Presets) => {
    // console.warn('presetsModel changed, updating listController', presetsModel.data);
    listController.updateList(presetsModel.data);
  });

  const loadPresetAction = (presetName: string, notification: boolean) => {
    setHiliteFile(undefined);
    return loadPreset({ name: presetName }, t, notification).then((res) => {
      if (res.ok) {
        shouldReloadData();
        triggerEvent('preset-load');
      }
      return res;
    });
  };

  const clickLoadPresetAction = (presetName: string, notification: boolean) => {
    // show modal if unsaved changes
    
    // PROMPT IF UNSAVED
    const wantedPresetId = `${presetName}.cfg`;
    const presetModel = listController.list.find((p) => p.name === wantedPresetId);
    // console.log('Active preset modified:', presets.activeWasModified, 'Selected preset:', presetName, 'Active preset:', presets.active);
    if (presets.activeWasModified && wantedPresetId === presets.active) { 
      // Show modal with just 2 buttons NKE-3690
        setSelectedFile(presetModel);
        setPresetLoadParam2(notification);
        showReloadWarningModal();
        return null;
    } else if (presets.activeWasModified && wantedPresetId !== presets.active) {      
      if (presetModel) {
        setSelectedFile(presetModel);
        setPresetLoadParam2(notification);
        showSavePromptFromLoadModal();
        return null;
      }
    } 

    return loadPresetAction(presetName, notification).then((res) => {
      if (!res.ok && !notification) {
        createAndDispatchNotification(t('presets.notifications.loadErrorMsg'), NotificationVariant.ERROR);
      }
    });
  };

  const proceedLoad = (selectedFile: Preset) => {
    const notification = presetLoadParam2();
    return loadPresetAction(selectedFile.name, notification).then((res) => {
      if (!res.ok && !notification) {
        createAndDispatchNotification(t('presets.notifications.loadErrorMsg'), NotificationVariant.ERROR);
      }
    });
  }
  
  const cancelLoad = () => {
    hideSavePromptFromLoadModal();
    hideReloadWarningModal();
  };

  //TODO this one could return a promise and then we finalize on the promise end.
  const uploadAndloadPresetAction = (file: File) => {
    setImportButtonState('pending');
    if (activateImportedPreset) {
      uploadPreset(file, t, false).then((res) => {
        if (!res.ok) {
          setImportButtonState(undefined);
          triggerEvent('enable-preset-fetch');
          createAndDispatchNotification(
            t('presets.notifications.importConfigurationErrorMsg'),
            NotificationVariant.ERROR,
          );
          return;
        }
        loadPresetAction(file.name, false).then((res) => {
          if (res.ok) {
            if (!presets.autosave) {
              setStartupPreset({ name: file.name }, t, false).then((res) => {
                setImportButtonState(undefined);
                triggerEvent('enable-preset-fetch');
                notificationHandler(
                  res,
                  t('presets.notifications.importAndLoadSuccessMsg', { name: file.name }),
                  t('presets.notifications.importAndLoadErrorMsg', { name: file.name }),
                  t,
                );
                setHiliteFile(file.name);
                //FIXME maybe some setTimeout to fade ? If we are still rendered
              });
            } else {
              setImportButtonState(undefined);
              triggerEvent('enable-preset-fetch');
              notificationHandler(
                res,
                t('presets.notifications.importAndLoadSuccessMsg', { name: file.name }),
                t('presets.notifications.importAndLoadErrorMsg', { name: file.name }),
                t,
              );
            }
          } else {
            createAndDispatchNotification(t('presets.notifications.loadErrorMsg'), NotificationVariant.ERROR);
            setImportButtonState(undefined);
          }
        });
      });
    } else {
      uploadPreset(file, t, true).then(() => {
        setImportButtonState(undefined);
        triggerEvent('enable-preset-fetch');
        setHiliteFile(file.name);
        shouldReloadData();
      });
    }
  };

  const handleImportPreset = (file: File): void => {
    handleImportShown();
    triggerEvent('stop-preset-fetch');
    if (activateImportedPreset && presets.activeWasModified && file.name !== presets.active) {
      setSelectedFile(file);
      showSavePromptModal();
    } else {
      uploadAndloadPresetAction(file);
    }
  };

  const handleImportShown = (): void => {
    hideImport();
    shouldReloadData();
  };

  const reloadPresetData = (): void => {
    listController.unselectAll();
    shouldReloadData();
  };

  const handleSetAsStartup = (preset: PresetViewModel) => {
    setStartupPreset(preset, t, true).then((res) => {
      if (res.ok) {
        reloadPresetData();
      }
      //FIXME : What happens if the setStartupPreset doesn't have a "res.ok"
    });
  };

  const handleSetAutoSave = (checked: boolean) => {
    setAutoSavePreset(checked, t).then((res) => {
      if (res.ok) {
        reloadPresetData();
      }
      //FIXME : What happens if the setAutoSavePreset doesn't have a "res.ok"
    });
  };

  const handleNew = (preset: PresetViewModel, overwrite: boolean): void => {
    setHiliteFile(undefined);
    createPreset(
      {
        ...preset,
        name: `${preset.name}.cfg`,
        startup: preset.startup,
        changeActive: preset.startup,
      },
      overwrite,
      t,
    ).then((res) => {
      if (res.ok) {
        hideNew();
        fetchPresets(t).then(() => {
          setTimeout(() => {
            scrollToId(`${preset.name}.cfg`, 48);
          }, 300);
        });
      }
    });
  };

  const onSuccessDelete = (): void => {
    setHiliteFile(undefined);
    hideDelete();
    listController.unselectAll();
    setSingleItem(undefined);
    shouldReloadData();
    //setSelectAll(false); // FIXME, needed?
  };

  const handleRename = (preset: PresetViewModel, overwrite: boolean, originalName: string) => {
    setHiliteFile(undefined);
    const endsWithCfg = preset.name.trim().endsWith('.cfg');
    return renamePreset({ ...preset, name: endsWithCfg ? preset.name.trim() : `${preset.name.trim()}.cfg` }, overwrite, originalName, t).then((res) => {
      if (res.ok) {
        hideRename();
        shouldReloadData();
        listController.unselectAll();
      }
    });
  };

  const handleDuplicate = (item: Preset): void => {
    setHiliteFile(undefined);
    duplicatePreset(item, t).then((res) => {
      if (res.ok) {
        shouldReloadData();
        listController.unselectAll();
      }
    });
  };
  if (isNil(presets)) {
    return null;
  }

  const content = (): ReactNode => {
    return (
      <form className="HaiForm" id="preset-manager">
        <div className="d-flex">
          <FormSwitch
            name="autosave"
            switchLabel={t('presets.autoSave')}
            checked={presets.autosave}
            onChange={handleSetAutoSave}
            className="hai-mb-2 ml-auto"
          />
        </div>
        <PresetManagerTable
          listController={listController}
          presets={presets}
          hilited={hiliteFile}
          onDelete={showDelete}
          onDuplicate={handleDuplicate}
          onLoadClicked={clickLoadPresetAction}
          onRename={showRename}
          onSetAsStartup={presets.autosave ? null : handleSetAsStartup}
          shouldReloadData={shouldReloadData}
          setSingleItem={setSingleItem}
        />
        {!importShown && (
          <div className="d-flex hai-mt-8">
            <Button state={importButtonState} onClick={showImport}>
              {importShown ? t('general.cancel') : t('general.import')}
            </Button>
            {SHOW_NEW_PRESET_BUTTON && <Button onClick={showNew}>{t('presets.modal.form.newTitle')}</Button>}
            <Button
              variant="primary"
              className="ml-auto"
              onClick={() => {
                listController.unselectAll();
                onCancel();
              }}
            >
              {t('general.done')}
            </Button>
          </div>
        )}
        {importShown && (
          <PresetImport
            activateImportedPreset={activateImportedPreset}
            autosave={presets.autosave}
            handleImportPreset={handleImportPreset}
            setActivateImportedPreset={setActivateImportedPreset}
            onCancel={hideImport}
            presetNames={presetNames(presets.data.map((p) => p.name))}
          />
        )}
        {newShown && (
          <PresetEditModal
            autosave={presets.autosave}
            showSetAsStartupCheckbox={true}
            viewModel={newPresetViewModel()}
            presetNames={presetNames(presets.data.map((p) => p.name))}
            onSubmit={handleNew}
            onCancel={hideNew}
            title={t('presets.modal.form.newTitle')}
            btnText={t('general.create')}
          />
        )}
        {renameShown && (
          <PresetEditModal
            autosave={presets.autosave}
            showSetAsStartupCheckbox={false}
            viewModel={singleItem()}
            presetNames={presetNames(presets.data.map((p) => p.name))}
            onSubmit={handleRename}
            onCancel={() => {
              hideRename();
              listController.unselectAll();
            }}
            title={t('presets.modal.form.renameTitle')}
            btnText={t('general.rename')}
          />
        )}
        {deleteShown && (
          <PresetDeleteModal
            itemsToDelete={singleItem() ? [singleItem()] : listController.selectedRows()}
            onCancel={() => {
              hideDelete();
              setSingleItem(undefined);
            }}
            onSuccessDelete={onSuccessDelete}
          />
        )}
        {savePromptModalShown && (
          //TODO: Rename modal, find better name
          <RebootNeededNotSavedModal
            onCancel={hideSavePromptModal}
            onReboot={() => {
              //import
              hideSavePromptModal();
              uploadAndloadPresetAction(selectedFile());
            }}
            onSave={() => {
              //Save and import
              hideSavePromptModal();
              triggerEvent('stop-preset-fetch');
              triggerEvent('preset-save-callback', {
                offerSetAsStartup: false,
                callback: (_res: any) => {
                  uploadAndloadPresetAction(selectedFile()); // Import and load new preset file
                },
              });
            }}
            bodyText={
              <>
                {t('upload.presetUploadNotSavedModal.body1')}
                <br />
                {t('upload.presetUploadNotSavedModal.body2')}
              </>
            }
            middleLabel={t('upload.import')}
            lastLabel={t('upload.saveAndImport')}
            middleVariant="secondary"
            lastVariant="primary"
          />
        )}
        {savePromptFromLoadModalShown && (
                      //TODO: Rename modal, find better name
                      <RebootNeededNotSavedModal
                        onCancel={cancelLoad}
                        onReboot={() => {
                          //import
                          hideSavePromptFromLoadModal();
                          proceedLoad(selectedFile());
                        }}
                        onSave={() => {
                          //Save and import
                          hideSavePromptFromLoadModal();
                          triggerEvent('stop-preset-fetch');
                          triggerEvent('preset-save-callback', {
                            offerSetAsStartup: false,
                            callback: (_res: any) => {
                              proceedLoad(selectedFile()); // Import and load new preset file
                            },
                          });
                        }}
                        bodyText={
                          <>
                            {t('upload.presetLoadNotSavedModal.body1')}
                            <br />
                            {t('upload.presetLoadNotSavedModal.body2')}
                          </>
                        }
                        middleLabel={t('general.load')}
                        lastLabel={t('upload.saveAndLoad')}
                        middleVariant="secondary"
                        lastVariant="primary"
                      />
                    )}
                    
        {reloadWarningModalShown && (
                      <TwoButtonsModal
                        onCancel={cancelLoad}
                        onPrimary={() => {
                          //import
                          hideReloadWarningModal();
                          proceedLoad(selectedFile());
                        }}
                        title={t('presets.loadPresetConfirmTitle')}
                        bodyText={
                          <>
                            {t('presets.loadPresetConfirmBody')}
                          </>
                        }
                        primaryLabel={t('general.load')}
                        primaryVariant="primary"
                      />
                    )}
      </form>
    );
  };

  return (
    <Dialog
      accentColor={constant.productColor}
      title={t('presets.presetManager')}
      onClose={() => {
        listController.unselectAll();
        onCancel();
      }}
      show={true}
      dialogType="activity"
      headerIcon="Presets"
      content={content()}
      autoScroll={false}
      size="lg"
      className="mk-modal-extra-large"
    />
  );
};
